import React, { useEffect, useState } from 'react';
import { getRequest, deleteRequest, patchRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FaFilePdf } from 'react-icons/fa';

const ListNews = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNews = async () => {
        try {
            setLoading(true);
            const res = await getRequest('news?all=true');
            setNews(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch news:', err);
            toast.error("Failed to load news list.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this news item?")) {
            try {
                await deleteRequest(`news/${id}`);
                toast.success("News deleted successfully!");
                fetchNews();
            } catch (err) {
                console.error(err);
                toast.error("Failed to delete news item.");
            }
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            await patchRequest(`news/${id}`, { is_active: !currentStatus });
            toast.success("News status updated successfully!");
            fetchNews();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status.");
        }
    };

    const tableHeaders = [
        {
            Header: 'ID',
            accessor: 'id',
        },
        {
            Header: 'News Title',
            accessor: 'title',
            Cell: ({ row }) => (
                <div className="text-left font-weight-bold">
                    {row.original.title}
                </div>
            )
        },
        {
            Header: 'Type',
            accessor: 'type',
            Cell: ({ row }) => (
                <span className="badge badge-primary text-capitalize px-2 py-1" style={{ fontSize: '12px' }}>
                    {row.original.type}
                </span>
            )
        },
        {
            Header: 'Published At',
            accessor: 'publishedAt',
            Cell: ({ row }) => (
                <span>
                    {row.original.publishedAt ? new Date(row.original.publishedAt).toLocaleDateString() : 'N/A'}
                </span>
            )
        },
        {
            Header: 'Attachment',
            accessor: 'file',
            Cell: ({ row }) => (
                row.original.file ? (
                    <a
                        href={`${process.env.REACT_APP_BACKEND}uploads/${row.original.file}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-danger"
                    >
                        <FaFilePdf size={16} className="mr-1" /> View Circular
                    </a>
                ) : (
                    <span className="text-muted" style={{ fontSize: '12px' }}>No file</span>
                )
            )
        },
        {
            Header: 'Status',
            accessor: 'is_active',
            Cell: ({ row }) => (
                <button
                    className={`btn btn-sm ${row.original.is_active ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => handleStatusToggle(row.original.id, row.original.is_active)}
                    style={{ minWidth: '85px' }}
                >
                    {row.original.is_active ? 'Active' : 'Inactive'}
                </button>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <div className="d-flex justify-content-center">
                    <Link
                        className="btn btn-sm btn-info mr-2"
                        to={`/news/update/${row.original.id}`}
                    >
                        Edit
                    </Link>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(row.original.id)}
                    >
                        Delete
                    </button>
                </div>
            ),
        }
    ];

    useEffect(() => {
        fetchNews();
    }, []);

    return (
        <>
            <div className="container-fluid mt-4">
                {/* Premium Animated Header Banner */}
                <div 
                    className="dashboard-header-card d-flex align-items-center justify-content-between mb-4 mx-2 shadow-lg" 
                    style={{ 
                        background: 'linear-gradient(135deg, #0d47a1 0%, #00695c 100%)', 
                        padding: '1.5rem 2rem', 
                        borderRadius: '15px',
                        animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                >
                    <div className="d-flex align-items-center">
                        <div className="icon-circle mr-3" style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa fa-newspaper text-white" style={{ fontSize: '24px' }}></i>
                        </div>
                        <div>
                            <h3 className="text-white font-weight-bold mb-0" style={{ letterSpacing: '0.5px' }}>News & Announcements</h3>
                            <p className="text-white-50 mb-0 small">Manage your latest news and updates</p>
                        </div>
                    </div>
                    <Link 
                        className="btn btn-light font-weight-bold shadow-sm" 
                        to="/news/add"
                        style={{ color: '#0d47a1', borderRadius: '8px', padding: '10px 20px', transition: 'all 0.3s' }}
                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }} onFocus={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }} onBlur={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        <i className="fa fa-plus mr-2"></i>
                        Add New News
                    </Link>
                </div>
                
                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : news.length > 0 ? (
                    <AdminTable tableHeaders={tableHeaders} tableData={news} />
                ) : (
                    <div className="text-center my-5">
                        <p>No news items found.</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default ListNews;
